// P9/S8 — İK Asistanı panel (spec 70 feat_support_ai_assistant +
// ai_capabilities.ai_help_assistant).
//
// Mounted ONCE, by the app shell (2026-08-04): this is the product's only AI
// surface. It replaced the Faz-0 AiDrawer, which posted to /v1/ai/stream with
// NO capability_id — the unmanaged raw-prompt path behind
// AI_RAW_PROMPT_STREAM_ENABLED, off in every environment, so the global drawer
// could only ever draw an error. This panel sends capability_id and the
// support-assistant route owns the whole chain (auth, permission, flag, rate
// limit, ACL-filtered retrieval, guardrail, citations/refusal, audit).
//
//   * Open state lives in the UI store — the TopBar button is the single
//     launcher on every screen (there is no second floating launcher).
//   * Streaming: consumes the /v1/ai/stream capability SSE (api.ts). Each
//     new question ABORTS the in-flight stream (abort-race-guard) and the
//     panel unmount aborts too.
//   * Citations: each chip is an ANCHOR to /help?article=<id>. One code path
//     for both cases — on /help that re-renders the same screen with its
//     reader pane open (no reload), and from anywhere else it takes the
//     caller to the Help Center already reading the cited article. A host
//     callback would only work for the one screen that could pass it.
//   * Answers are MARKDOWN (SEM-6345): the model writes headings, numbered
//     lists and bold, and the panel used to print the syntax. MarkdownBody
//     (features/support-help/markdown) draws a safe subset as React elements —
//     model output is untrusted, so there is no HTML pass-through to sanitize
//     — and its text decorator turns screen names the caller's own menu
//     carries into links to those screens (./screen-links).
//   * Refusal: a CLEAN generic message (the wire carries only an i18n KEY —
//     never what was hidden) + the escalation CTA. Escalation is
//     USER-CONFIRMED: the CTA opens a confirm dialog; only the explicit
//     confirm calls the escalate endpoint (the assistant never opens a
//     ticket by itself — spec AC). The idempotency key is minted once per
//     dialog so a retry replays instead of duplicating.
//   * Size is FIXED, not content-derived (2026-08-05). It was
//     `max-h-[70vh]` with no height, so an empty thread collapsed the
//     transcript to nothing and the whole panel opened as a ~110px strip —
//     the launcher looked broken rather than empty. A chat surface has to be
//     the same shape before and after the first message.
//   * The transcript is spec 48's own chat surface: ChatBubble (user +
//     assistant), StreamingCursor and RefusalPanel. Spec 48's CitationChip is
//     deliberately NOT used — it renders a <button> (see the citations below).
//   * Empty thread carries prompt suggestions (spec 48
//     prompt_suggestion_rail). They POPULATE the composer, never auto-send,
//     and they stay inside what this assistant can actually answer
//     (published help articles) — a suggestion that gets refused teaches the
//     reader the product is broken.
//   * Visibility: the shared availability query combines the build flag with
//     the current principal's fresh backend policy/provider status. Loading,
//     stale or failed status hides both panel and launcher, aborts the stream
//     and closes the panel. Identity changes also discard the transcript.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { Maximize2, Minimize2, Send, Sparkles, SquarePen, X } from "lucide-react";

import { useUiStore } from "@/store/ui";

import {
  Badge,
  Button,
  ChatBubble,
  FormField,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PromptSuggestionRail,
  RefusalPanel,
  Spinner,
  StreamingCursor,
  Textarea,
} from "@novemai/components";

// Deep imports, NOT the feature barrels: this panel is mounted by the eager
// shell, and a barrel would drag the Help Center screen into the main chunk.
import { MarkdownBody } from "@/features/support-help/markdown";
import { useScreenLinkDecorator } from "./screen-links";
import { useAiAvailability, useAiPrincipalKey } from "./availability";

import {
  askAssistant,
  AssistantDisabledError,
  escalateAssistant,
  type AssistantCitation,
  assistantCitationHref,
  type EscalatedTicket,
} from "./api";

/**
 * Example questions for an empty thread. Every one has to be answerable from
 * published help articles — this assistant is grounded, not general-purpose.
 * Four are shown; "other examples" rotates rather than randomises so the same
 * session never sees a chip disappear and reappear at random.
 */
const SUGGESTION_KEYS = [
  "assistant.suggestion_leave",
  "assistant.suggestion_payslip",
  "assistant.suggestion_overtime",
  "assistant.suggestion_onboarding",
  "assistant.suggestion_expense",
  "assistant.suggestion_remote",
] as const;
const SUGGESTIONS_SHOWN = 4;

/**
 * How long tokens accumulate before they are committed to the transcript.
 * The assistant bubble declares aria-live="polite" on its content while it
 * streams, and a live region is re-announced on every mutation — committing
 * each SSE delta on its own would read the growing answer back a few dozen
 * times over. Every delta still lands; they just do not each get a render.
 */
const ANNOUNCE_BATCH_MS = 500;

/** Refusal wire keys → panel copy (generic on purpose — no enumeration). */
const REFUSAL_COPY: Record<string, string> = {
  "ai.refusal.gate_unavailable": "assistant.refusal_generic",
  "ai.refusal.gate_tenant_disabled": "assistant.refusal_disabled",
  "ai.refusal.input_out_of_scope": "assistant.refusal_generic",
  "ai.refusal.input_injection": "assistant.refusal_blocked",
};

/** SEM-6346 referral wire keys → the "who does perform this" line rendered
 *  above the escalation CTA. Generic by role — never record-specific. */
const REFERRAL_COPY: Record<string, string> = {
  "ai.referral.hr_or_manager": "assistant.referral_hr_or_manager",
  "ai.referral.hr": "assistant.referral_hr",
};

interface AssistantTurn {
  readonly id: string;
  readonly role: "user" | "assistant";
  /** Epoch ms, formatted at render — a locale switch must reformat the whole
   *  transcript, not leave the turns taken before it in the old locale. */
  readonly createdAt: number;
  text: string;
  citations: readonly AssistantCitation[];
  /** Set when the assistant refused — the copy key to render + CTA. */
  refusalCopyKey: string | null;
  /** Set when the refusal also routes the caller to the right authority. */
  referralCopyKey: string | null;
  streaming: boolean;
  errored: boolean;
}

export { isSupportAssistantEnabled } from "./availability";

export function SupportAssistantRail(): React.ReactElement | null {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const decorateText = useScreenLinkDecorator();
  const aiAvailable = useAiAvailability();
  const principal = JSON.stringify(useAiPrincipalKey());
  const principalRef = useRef(principal);
  principalRef.current = principal;
  const previousPrincipalRef = useRef(principal);

  const open = useUiStore((s) => s.aiAssistantOpen);
  const setOpen = useUiStore((s) => s.setAiAssistantOpen);
  const [turns, setTurns] = useState<readonly AssistantTurn[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [suggestionOffset, setSuggestionOffset] = useState(0);
  // The backend owns a durable conversation identity. Keep it beside the
  // rendered transcript so follow-up asks and escalation use the same thread.
  const [threadId, setThreadId] = useState<string | null>(null);

  // Escalation dialog state. The idempotency key is minted once per OPEN.
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateSubject, setEscalateSubject] = useState("");
  const [escalateSummary, setEscalateSummary] = useState("");
  const [escalateKey, setEscalateKey] = useState("");
  const [escalatePending, setEscalatePending] = useState(false);
  const [escalateError, setEscalateError] = useState(false);
  const [escalatedTicket, setEscalatedTicket] = useState<EscalatedTicket | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!aiAvailable) {
      abortRef.current?.abort();
      setOpen(false);
      setPending(false);
      setEscalateOpen(false);
      setTurns((current) => current.map((turn) => turn.streaming ? { ...turn, streaming: false, refusalCopyKey: "assistant.refusal_generic" } : turn));
    }
  }, [aiAvailable, setOpen]);
  useEffect(() => {
    if (previousPrincipalRef.current === principal) return;
    previousPrincipalRef.current = principal;
    abortRef.current?.abort();
    setOpen(false); setTurns([]); setThreadId(null); setInput("");
    setPending(false); setUnavailable(false); setEscalateOpen(false);
    setEscalateSubject(""); setEscalateSummary(""); setEscalatedTicket(null); setEscalatePending(false);
  }, [principal, setOpen]);

  // Unmount → abort any in-flight stream.
  useEffect(() => {
    return (): void => abortRef.current?.abort();
  }, []);

  // Keep the newest turn in view.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [turns]);

  const lastQuestion = useMemo(
    () => [...turns].reverse().find((m) => m.role === "user")?.text ?? "",
    [turns],
  );

  const suggestions = useMemo(
    () =>
      Array.from({ length: SUGGESTIONS_SHOWN }, (_, i) => {
        const key = SUGGESTION_KEYS[(suggestionOffset + i) % SUGGESTION_KEYS.length]!;
        return { id: key, text: t(key) };
      }),
    [suggestionOffset, t],
  );

  const timeFormat = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { hour: "2-digit", minute: "2-digit" }),
    [i18n.language],
  );

  if (!aiAvailable) return null;

  function patchTurn(id: string, patch: Partial<AssistantTurn>): void {
    setTurns((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }

  /** Drops the thread id too: keeping it would attach the next question to a
   *  server-side conversation the reader believes they just ended. */
  function startNewThread(): void {
    abortRef.current?.abort();
    setPending(false);
    setTurns([]);
    setThreadId(null);
    setInput("");
  }

  async function ask(): Promise<void> {
    const question = input.trim();
    // No `pending` guard (S8 review M2): last-question-wins is REAL — a new
    // ask supersedes the in-flight stream via the abort below.
    if (question === "") return;
    setInput("");

    // Last-question-wins: a new ask aborts the previous in-flight stream.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const answerId = crypto.randomUUID();
    const askedAt = Date.now();
    setTurns((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: "user",
        createdAt: askedAt,
        text: question,
        citations: [],
        refusalCopyKey: null,
        referralCopyKey: null,
        streaming: false,
        errored: false,
      },
      {
        id: answerId,
        role: "assistant",
        createdAt: askedAt,
        text: "",
        citations: [],
        refusalCopyKey: null,
        referralCopyKey: null,
        streaming: true,
        errored: false,
      },
    ]);
    setPending(true);
    let buffered = "";
    // Zero so the FIRST delta commits at once: there is nothing on screen to
    // re-announce yet, and holding it back would only make the answer look
    // half a second slower than it is.
    let committedAt = 0;
    const commitBuffered = (): void => {
      if (buffered === "") return;
      const chunk = buffered;
      buffered = "";
      committedAt = Date.now();
      setTurns((prev) => prev.map((m) => (m.id === answerId ? { ...m, text: m.text + chunk } : m)));
    };
    try {
      for await (const event of askAssistant(question, {
        signal: controller.signal,
        ...(threadId !== null && { threadId }),
      })) {
        if (controller.signal.aborted) return;
        if (event.type === "delta") {
          buffered += event.text;
          if (Date.now() - committedAt >= ANNOUNCE_BATCH_MS) commitBuffered();
        } else if (event.type === "citations") {
          patchTurn(answerId, { citations: event.citations });
        } else if (event.type === "refusal") {
          patchTurn(answerId, {
            refusalCopyKey: REFUSAL_COPY[event.message_key] ?? "assistant.refusal_generic",
            referralCopyKey:
              event.referral_key === undefined
                ? null
                : (REFERRAL_COPY[event.referral_key] ?? null),
          });
        } else if (event.type === "error") {
          if (event.thread_id !== undefined) setThreadId(event.thread_id);
          patchTurn(answerId, { errored: true });
        } else if (event.type === "done" && event.thread_id !== undefined) {
          setThreadId(event.thread_id);
        }
      }
      commitBuffered();
      patchTurn(answerId, { streaming: false });
    } catch (err) {
      if (controller.signal.aborted) return; // superseded by a newer ask / close
      if (err instanceof AssistantDisabledError) {
        setUnavailable(true);
        setTurns((prev) => prev.filter((m) => m.id !== answerId));
      } else {
        patchTurn(answerId, { streaming: false, errored: true });
      }
    } finally {
      if (abortRef.current === controller) setPending(false);
    }
  }

  /** Transcript summary — the escalation ticket's first message (spec wf). */
  function buildSummary(): string {
    const lines: string[] = [];
    for (const turn of turns) {
      if (turn.role === "user") {
        lines.push(`${t("assistant.transcript_question_prefix")} ${turn.text}`);
      } else if (turn.refusalCopyKey !== null) {
        lines.push(t("assistant.transcript_refusal_line"));
      } else if (turn.text.trim() !== "") {
        lines.push(turn.text.trim());
      }
    }
    return lines.join("\n\n");
  }

  function openEscalate(): void {
    setEscalateSubject(lastQuestion.slice(0, 200));
    setEscalateSummary(buildSummary());
    setEscalateKey(crypto.randomUUID()); // one key per confirmation dialog
    setEscalateError(false);
    setEscalatedTicket(null);
    setEscalateOpen(true);
  }

  async function confirmEscalate(): Promise<void> {
    if (escalatePending) return;
    const requestPrincipal = principal;
    setEscalatePending(true);
    setEscalateError(false);
    try {
      const ticket = await escalateAssistant(
        {
          subject: escalateSubject.trim(),
          summary_markdown: escalateSummary.trim(),
          category: "other",
          ...(threadId !== null && { thread_id: threadId }),
        },
        escalateKey,
      );
      if (principalRef.current === requestPrincipal) setEscalatedTicket(ticket);
    } catch (err) {
      if (principalRef.current !== requestPrincipal) return;
      if (err instanceof AssistantDisabledError) setUnavailable(true);
      setEscalateError(true);
    } finally {
      if (principalRef.current === requestPrincipal) setEscalatePending(false);
    }
  }

  // Closed = absent. The launcher is the TopBar button (one affordance, every
  // screen); a second floating one would double the same control.
  if (!open) return null;

  return (
    <aside
      aria-label={t("assistant.panel_title")}
      // Height is DECLARED, not inherited from the transcript — see the header
      // note. Both axes clamp against the viewport so the panel never opens
      // taller or wider than the window on a laptop or a tablet.
      className={`fixed bottom-6 right-6 z-40 flex flex-col overflow-hidden rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-[var(--elevation-overlay-shadow)] ${
        expanded
          ? "h-[calc(100vh-64px)] w-[min(880px,calc(100vw-48px))]"
          : "h-[min(660px,calc(100vh-96px))] w-[min(440px,calc(100vw-32px))]"
      }`}
    >
      {/* header */}
      <div className="flex items-center gap-1.5 border-b border-[var(--border-subtle)] bg-[var(--color-accent-950)] px-4 py-3">
        <Sparkles size={16} aria-hidden="true" className="text-[var(--color-accent-200)]" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-[var(--color-neutral-50)]">
            {t("assistant.panel_title")}
          </p>
          <p className="truncate text-[12px] text-[var(--color-accent-200)]">
            {t("assistant.panel_subtitle")}
          </p>
        </div>
        {turns.length > 0 && (
          <button
            type="button"
            onClick={startNewThread}
            aria-label={t("assistant.new_thread")}
            className="rounded-lg p-1.5 text-[var(--color-accent-200)] hover:bg-[var(--color-accent-900)]"
          >
            <SquarePen size={16} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          onClick={(): void => setExpanded(!expanded)}
          aria-label={expanded ? t("assistant.collapse") : t("assistant.expand")}
          aria-pressed={expanded}
          className="rounded-lg p-1.5 text-[var(--color-accent-200)] hover:bg-[var(--color-accent-900)]"
        >
          {expanded ? (
            <Minimize2 size={16} aria-hidden="true" />
          ) : (
            <Maximize2 size={16} aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          onClick={(): void => {
            // Close aborts the in-flight stream too (S8 review M2) — no
            // background state updates after the panel is dismissed.
            abortRef.current?.abort();
            setPending(false);
            setOpen(false);
          }}
          aria-label={t("assistant.close_label")}
          className="rounded-lg p-1.5 text-[var(--color-accent-200)] hover:bg-[var(--color-accent-900)]"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      {/* transcript */}
      <div ref={scrollRef} className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
        {unavailable ? (
          <div className="rounded-xl bg-[var(--bg-subtle)] p-3">
            <p className="text-[14px] font-semibold text-[var(--fg-primary)]">
              {t("assistant.unavailable_title")}
            </p>
            <p className="mt-1 text-[13px] text-[var(--fg-secondary)]">
              {t("assistant.unavailable_description")}
            </p>
          </div>
        ) : turns.length === 0 ? (
          <div className="flex flex-1 flex-col justify-end gap-4">
            <div>
              <p className="font-serif text-[20px] text-[var(--fg-primary)]">
                {t("assistant.empty_title")}
              </p>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-[var(--fg-secondary)]">
                {t("assistant.empty_description")}
              </p>
            </div>
            <PromptSuggestionRail
              suggestions={suggestions}
              railAriaLabel={t("assistant.suggestions_label")}
              refreshLabel={t("assistant.suggestions_refresh")}
              // Populates the composer — spec 48 forbids auto-send, and the
              // reader gets to edit the question into their own words.
              onSuggestionPick={(s): void => setInput(s.text)}
              onRefresh={(): void =>
                setSuggestionOffset((prev) => (prev + SUGGESTIONS_SHOWN) % SUGGESTION_KEYS.length)
              }
            />
          </div>
        ) : (
          turns.map((turn) => {
            const time = timeFormat.format(turn.createdAt);
            if (turn.role === "user") {
              return (
                <ChatBubble
                  key={turn.id}
                  variant="user"
                  content={turn.text}
                  timestamp={time}
                  // The component defaults its aria-label to English prose. A
                  // design system with no i18n runtime can only be told, and a
                  // default that leaks reads as English on a Turkish screen.
                  aria-label={t("assistant.bubble_user_label", { time })}
                />
              );
            }
            if (turn.refusalCopyKey !== null) {
              return (
                <RefusalPanel
                  key={turn.id}
                  // "other": the wire carries a copy key, never the class of
                  // what was withheld — naming permission or policy here would
                  // disclose exactly what the refusal is built to hide.
                  kind="other"
                  title={t("assistant.refusal_title")}
                  panelAriaLabel={t("assistant.refusal_panel_label")}
                  alternativesAriaLabel={t("assistant.refusal_next_steps_label")}
                  // The referral rides the reason (a 1–2 sentence explanation
                  // by contract) because it has to sit ABOVE the escalation
                  // CTA — the copy points at "the button below" — and the
                  // panel's only slot before the alternatives is this one.
                  reason={
                    turn.referralCopyKey === null
                      ? t(turn.refusalCopyKey)
                      : `${t(turn.refusalCopyKey)} ${t(turn.referralCopyKey)}`
                  }
                  alternatives={[
                    { id: "escalate", label: t("assistant.escalate_cta"), onClick: openEscalate },
                  ]}
                />
              );
            }
            return (
              <ChatBubble
                key={turn.id}
                variant="assistant"
                timestamp={time}
                aria-label={t("assistant.bubble_assistant_label", { time })}
                isStreaming={turn.streaming}
                content={
                  turn.errored ? (
                    <span role="alert" className="text-[var(--state-danger)]">
                      {t("assistant.error_generic")}
                    </span>
                  ) : turn.text === "" && turn.streaming ? (
                    <span className="flex items-center gap-2 text-[var(--fg-muted)]">
                      <Spinner size="sm" aria-label={t("assistant.thinking")} />{" "}
                      {t("assistant.thinking")}
                    </span>
                  ) : (
                    <MarkdownBody
                      markdown={turn.text}
                      decorateText={decorateText}
                      // The panel's own body face — the renderer's default is
                      // the article reader's serif.
                      className="font-sans text-[14px]"
                    />
                  )
                }
                // Only once tokens are on screen: beside the "preparing an
                // answer" spinner it would be a second thing saying the same.
                {...(turn.streaming &&
                  turn.text !== "" &&
                  !turn.errored && { streamingCursor: <StreamingCursor /> })}
                {...(turn.citations.length > 0 && {
                  citationFooter: (
                    <>
                      <p className="text-[12px] font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
                        {t("assistant.sources_title")}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {turn.citations.map((c) => (
                          // A citation is the assistant's evidence. Reading it
                          // must not cost the conversation: as a <button> the
                          // only way to open the source was to leave the answer,
                          // and the rail's turns do not survive the trip. An
                          // anchor lets the reader open the article beside the
                          // answer it justifies — which is why spec 48's
                          // CitationChip (a button) is not what renders here.
                          <Link
                            key={assistantCitationHref(c)}
                            to={assistantCitationHref(c)}
                            aria-label={"source_kind" in c ? t("assistant.open_source", { title: c.title }) : t("assistant.open_article", { title: c.title })}
                            className="max-w-full truncate rounded-full border border-[var(--border-default)] bg-[var(--bg-surface)] px-2.5 py-1 text-[12px] text-[var(--accent)] no-underline hover:bg-[var(--accent-subtle-bg)]"
                          >
                            {c.title}
                          </Link>
                        ))}
                      </div>
                    </>
                  ),
                })}
              />
            );
          })
        )}
      </div>

      {/* composer */}
      {!unavailable && (
        <form
          className="border-t border-[var(--border-subtle)] px-3 py-2.5"
          onSubmit={(e): void => {
            e.preventDefault();
            void ask();
          }}
        >
          <div className="flex items-end gap-2">
            <Textarea
              rows={2}
              value={input}
              onChange={(e): void => setInput(e.target.value)}
              // A one-line Input made a question longer than the box
              // unreviewable before sending. Enter still sends, so the shortcut
              // people already have does not change; Shift+Enter is the escape
              // hatch the hint below names.
              onKeyDown={(e): void => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void ask();
                }
              }}
              placeholder={t("assistant.input_placeholder")}
              aria-label={t("assistant.input_label")}
              className="resize-none"
            />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={input.trim() === ""}
              aria-label={t("assistant.send")}
            >
              {/* pending only changes the ICON — submit stays enabled so a new
                  question can supersede the in-flight stream (S8 review M2). */}
              {pending ? (
                <Spinner size="sm" aria-label={t("assistant.thinking")} />
              ) : (
                <Send size={15} aria-hidden="true" />
              )}
            </Button>
          </div>
          <p className="mt-1.5 text-[12px] text-[var(--fg-muted)]">
            {t("assistant.composer_hint")}
          </p>
        </form>
      )}

      {/* escalation confirm dialog (USER-CONFIRMED mutation — spec AC) */}
      <Modal open={escalateOpen} onClose={(): void => setEscalateOpen(false)}>
        <ModalHeader
          title={`${t("assistant.escalate_title")} ${t("assistant.escalate_title_em")}`}
        />
        <ModalBody>
          {escalatedTicket === null ? (
            <div className="space-y-3">
              <p className="text-[13px] text-[var(--fg-secondary)]">
                {t("assistant.escalate_description")}
              </p>
              <FormField label={t("assistant.escalate_subject_label")} required>
                <Input
                  value={escalateSubject}
                  onChange={(e): void => setEscalateSubject(e.target.value)}
                  placeholder={t("assistant.escalate_subject_placeholder")}
                />
              </FormField>
              <FormField label={t("assistant.escalate_summary_label")}>
                <Textarea
                  rows={5}
                  value={escalateSummary}
                  onChange={(e): void => setEscalateSummary(e.target.value)}
                />
              </FormField>
              {escalateError && (
                <p role="alert" className="text-[13px] text-[var(--state-danger)]">
                  {t("assistant.escalate_error")}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Badge tone="success">{t("assistant.escalate_success")}</Badge>
              <p className="text-[13px] text-[var(--fg-secondary)]">{escalatedTicket.subject}</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {escalatedTicket === null ? (
            <>
              <Button variant="secondary" size="sm" onClick={(): void => setEscalateOpen(false)}>
                {t("assistant.escalate_cancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={escalateSubject.trim() === "" || escalateSummary.trim() === "" || escalatePending}
                onClick={(): void => void confirmEscalate()}
              >
                {t("assistant.escalate_confirm")}
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={(): void => {
                setEscalateOpen(false);
                void navigate("/me/support");
              }}
            >
              {t("assistant.escalate_view_ticket")}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </aside>
  );
}
